<template>
  <v-container fluid>
    <page-bar :title="pageTitle" />

    <div class="px-8 py-4">
      <!-- Categories -->
      <div class="subtitle-1 font-weight-bold mb-2 white--text">Categories</div>
      <grid-system :columns="{ xs: 1, sm: 2, md: 3, lg: 4 }" :gap="16">
        <div v-for="(category, index) in sortedCategories" :key="index">
          <card-section
            dense
            :title="category.name"
            :imgUrl="category.thm ? category.thm : null"
            :icon="category.thm ? null : 'mdi-camera'"
            :selected="index === selectedCategoryIdx"
            @click="OnCategorySelected(index)"
          />
        </div>
      </grid-system>

      <!-- Results -->
      <div class="subtitle-1 font-weight-bold mt-6 mb-2 white--text">
        Results
      </div>
      <v-row v-if="selectedCategoryIdx === -1">
        <v-col cols="12" class="white--text">
          Select a category to view results.
        </v-col>
      </v-row>

      <div v-else>
        <!-- Filters: attribute-based narrowing -->
        <div class="mb-4" v-if="filterAttributes.length">
          <v-card class="elevation-1" color="primaryDark" dark tile>
            <v-card-text class="pa-4">
              <grid-system :columns="{ xs: 1, sm: 2, md: 3 }" :gap="12">
                <div v-for="(attr, idx) in filterAttributes" :key="attr.id">
                  <attribute-field
                    :attribute="attr"
                    v-on:OnChanged="OnAttributeFieldChange"
                    :categories="selectedCategory ? [selectedCategory] : []"
                    :defaultItems="GetAttributeDataForExistingSearch(attr)"
                    :cascadingAttributes="selectedAttributes"
                    :isSearchAttribute="true"
                    :searchSectionId="searchData && searchData.sectionid"
                  />
                </div>
              </grid-system>
            </v-card-text>
          </v-card>
        </div>
        <grid-system :columns="{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }" :gap="16">
          <div v-for="(asset, index) in searchResults.results" :key="index">
            <AssetCard
              :asset="asset"
              :showSelect="inventoryActive"
              v-on:AssetSelected="OnAssetSelected"
              :selected="selectedAssets.indexOf(asset.id) > -1"
              :isUrlLink="true"
            />
          </div>
        </grid-system>

        <div class="mt-4">
          <infinite-loading
            :identifier="infiniteIncrementor"
            @infinite="infiniteHandler"
          >
            <div slot="spinner">Loading...</div>
            <div slot="no-more"></div>
            <div slot="no-results"></div>
          </infinite-loading>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import CONFIG from "@/appconfig.json";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import InfiniteLoading from "vue-infinite-loading";
import AssetCard from "../../cards/AssetCard";
import PageBar from "../../shared/PageBar";
import CardSection from "../../cards/CardSection";
import GridSystem from "../../shared/GridSystem";
import AttributeField from "../../attributes/AttributeField";

export default {
  name: "Catalogue",
  components: {
    InfiniteLoading,
    AssetCard,
    PageBar,
    CardSection,
    GridSystem,
    AttributeField,
  },
  props: {
    //Name of active section
    sectionName: {
      default: "",
      type: String,
    },
    //id of active catalogue
    catalogueid: {
      type: String,
    },
  },
  computed: {
    ...mapState("assetxpstate", [
      "sectionHeaders",
      "searchResults",
      "searchInProgress",
      "searchData",
      "catalogueCachedData",
      "inventoryActive",
      "markets",
    ]),
    ...mapGetters("assetxpstate", ["effectiveMarketId"]),
    pageTitle() {
      return this.sectionName + " Catalogue";
    },
    selectedCategory() {
      if (!this.section || !this.section.categories) return null;
      if (this.selectedCategoryIdx < 0) return null;
      return this.section.categories[this.selectedCategoryIdx] || null;
    },
    filterAttributes() {
      if (!this.searchData || !this.searchData.attributes) return [];
      const attrs = this.searchData.attributes;
      const preferredIds = [
        "attr-brand",
        "attr-manufacturer",
        "attr-sub-segment",
      ];
      const preferred = preferredIds
        .map((id) => attrs.find((a) => a.id === id))
        .filter(Boolean);
      const fallbacks = attrs.filter((a) => a.attributeType === 3);
      const combined = [...preferred, ...fallbacks];
      const seen = new Set();
      const result = [];
      for (const a of combined) {
        if (a && !seen.has(a.id)) {
          seen.add(a.id);
          result.push(a);
        }
        if (result.length >= 3) break;
      }
      return result;
    },

    sortedCategories() {
      if (this.section.categories != null) {
        return this.section.categories.sort((a, b) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        );
      } else {
        return [];
      }
    },
  },
  mounted: function() {
    this.ClearSearch();
    // Ensure markets are loaded before searching
    if (!this.markets || this.markets.length === 0) {
      this.GetMyMarkets().then(() => {
        this.loadCatalogueData();
      });
      return;
    }
    this.loadCatalogueData();
  },
  data: () => ({
    section: {},
    selectedCategoryIdx: -1,
    selectedAssets: [],
    infiniteIncrementor: 0,
    selectedAttributes: [],
  }),
  methods: {
    ...mapGetters("authentication", ["CanUserEditAssets", "GetAuthHeaderObj"]),

    ...mapActions("assetxpstate", [
      "CreateSection",
      "GetSectionData",
      "GetSearchResults",
      "GetSearchResultsByMarketId",
      "GetSearchData",
      "GetMyMarkets",
    ]),

    ...mapMutations("assetxpstate", [
      "ClearSearch",
      "AddAssetsToCurrentAssetList",
      "SetCachedCatalogueData",
    ]),
    loadCatalogueData() {
      this.GetSectionData(this.sectionName).then((section) => {
        this.section = section;
        this.GetSearchData(this.sectionName).then(() => {
          // Restore cached selected category if available
          const cached = this.catalogueCachedData.find(
            (c) => c.section === this.sectionName
          );
          if (
            cached &&
            cached.data &&
            typeof cached.data.selectedCategoryIdx === "number"
          ) {
            this.selectedCategoryIdx = cached.data.selectedCategoryIdx;
            if (this.selectedCategoryIdx !== -1) {
              // Trigger initial search load
              this.ClearSearch();
              this.infiniteIncrementor += 1;
            }
          }
        });
      });
    },
    OnCategorySelected(index) {
      this.selectedCategoryIdx = index;
      this.selectedAssets = [];
      this.selectedAttributes = [];
      this.ClearSearch();
      this.infiniteIncrementor += 1; // restart infinite loader

      // Cache selected category for this section
      this.SetCachedCatalogueData({
        section: this.sectionName,
        data: { selectedCategoryIdx: index },
      });
    },
    OnAttributeFieldChange(eventObj) {
      let updated = false;
      for (let i = 0; i < this.selectedAttributes.length; i++) {
        if (this.selectedAttributes[i].id === eventObj.attribute.id) {
          if (!eventObj.values || eventObj.values.length === 0) {
            this.selectedAttributes.splice(i, 1);
          } else if (eventObj.values[0] === "") {
            this.selectedAttributes.splice(i, 1);
          } else {
            this.selectedAttributes[i].values = eventObj.values;
            this.selectedAttributes[i].names = eventObj.names;
          }
          updated = true;
          break;
        }
      }
      if (!updated) {
        if (
          eventObj.values &&
          eventObj.values.length &&
          eventObj.values[0] !== ""
        ) {
          this.selectedAttributes.push({
            id: eventObj.attribute.id,
            attributeType: eventObj.attribute.attributeType,
            values: eventObj.values,
            names: eventObj.names,
          });
        }
      }
      this.Search();
    },
    GetAttributeDataForExistingSearch(attribute) {
      const existing = this.selectedAttributes.find(
        (e) => e.id === attribute.id
      );
      return existing ? existing : null;
    },
    AddSelectedToCurrentAssetList() {
      this.AddAssetsToCurrentAssetList(this.selectedAssets);
    },
    ClearSelection() {
      this.selectedAssets = [];
    },
    OnAssetSelected(eventObj) {
      var listingid = eventObj.id;
      var value = eventObj.value;
      var index = this.selectedAssets.indexOf(listingid);

      //if asset is not selected but was previously in the selected list
      if (value === false && index >= 0) {
        this.selectedAssets.splice(index, 1);
      }

      //if asset is selected and is not already in the selected list
      if (value === true && index < 0) {
        this.selectedAssets.push(listingid);
      }
    },
    Search() {
      this.ClearSearch();
      this.infiniteIncrementor += 1;
    },
    infiniteHandler($state) {
      if (this.selectedCategoryIdx === -1) {
        $state.complete();
        return;
      }

      const selected = this.section.categories[this.selectedCategoryIdx];
      const catids = [selected.id];
      const searchParams = {
        categories: catids,
        textSearch: "",
        attributes: this.selectedAttributes,
        token: this.searchResults.token,
        sectionid:
          (this.searchData && this.searchData.sectionid) || this.section.id,
        sectionName: this.$route.params["sectionName"],
        referedCategories: [],
        pageNumber: 1,
        pageCount: 24,
      };

      const hasMultipleMarkets =
        Array.isArray(this.markets) && this.markets.length > 1;

      if (hasMultipleMarkets && this.effectiveMarketId) {
        searchParams.marketId = this.effectiveMarketId;
      }

      const searchAction = hasMultipleMarkets && this.effectiveMarketId
        ? this.GetSearchResultsByMarketId
        : this.GetSearchResults;

      searchAction(searchParams).then(() => {
        if (this.searchResults.hasNextPage) {
          $state.loaded();
        } else {
          $state.complete();
        }
      });
    },
  },
  watch: {
    effectiveMarketId(newMarketId, oldMarketId) {
      // Trigger new search when effective market context changes
      if (newMarketId !== oldMarketId && this.selectedCategoryIdx !== -1) {
        this.ClearSearch();
        this.infiniteIncrementor += 1; // restart infinite loader
      }
    },
  },
};
</script>

<style></style>
